import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import random from 'lodash/random';

import { useMediaQuery } from '@react-hook/media-query';
import { ContentCardTestId } from 'src/shared/components/ContentCard';
import { Icon as DsIcon } from 'src/shared/ds/components/Icon';
import { SkeletonPlaceholder } from 'src/shared/ds/components/SkeletonPlaceholder';
import {
    BodyTextMedium,
    BodyTextSmall,
    BodyTextXSmall,
    HeadingText10,
    HeadingText20,
} from 'src/shared/ds/Typography';
import { dsColors, mediaQuery } from 'src/shared/ds/variables';
import { pxToRem } from 'src/shared/helpers/pxToRem';

export interface ContentCardItem {
    image: string;
    title: string;
    description: string;
    eyebrowText?: string;
    backgroundColor?: string;
    isCompleted?: boolean;
    isLocked?: boolean;
    imageInCircle?: boolean;
    onClick: () => void;
}

interface ContentCardProps extends ContentCardItem {
    isLastItem?: boolean;
    testID: string;
    horizontalScrollMode?: boolean;
    marginTop?: number;
}

const possibleColors = [
    dsColors.yellow800,
    dsColors.orange900,
    dsColors.blue800,
    dsColors.cyan900,
    dsColors.pink800,
];

export const ContentCard: React.FC<ContentCardProps> = ({
    image,
    title,
    description,
    onClick,
    testID,
    imageInCircle = false,
    eyebrowText,
    isCompleted,
    backgroundColor,
}) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));

    const TitleComponent = smallScreenDetected ? HeadingText10 : HeadingText20;
    const DescriptionComponent = smallScreenDetected ? BodyTextSmall : BodyTextMedium;

    const ImageComponent = imageInCircle ? CircleImage : Image;

    const [bgColorToUse, setBgColorToUse] = useState(backgroundColor);

    useEffect(() => {
        if (!backgroundColor) {
            setBgColorToUse(possibleColors[random(0, possibleColors.length - 1)]);
        }
    }, [backgroundColor]);

    return (
        <Wrapper onClick={onClick} data-testid={testID}>
            {isCompleted && (
                <Icon
                    data-testid={ContentCardTestId.Completed}
                    name="checkmark-enclosed-filled"
                    color="cyan500"
                />
            )}
            <ImageContainer css={{ backgroundColor: bgColorToUse }}>
                <ImageComponent src={image} />
            </ImageContainer>

            <ContentWrapper>
                {eyebrowText && (
                    <BodyTextXSmall fontColor="neutral400" marginBottom={4}>
                        {eyebrowText}
                    </BodyTextXSmall>
                )}
                <TitleComponent>{title}</TitleComponent>
                <DescriptionComponent
                    marginTop={smallScreenDetected ? 4 : 8}
                    fontColor="neutral400"
                >
                    {description}
                </DescriptionComponent>
            </ContentWrapper>
        </Wrapper>
    );
};

export const ContentCardSkeleton: React.FC = () => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));

    return (
        <Wrapper>
            <SkeletonPlaceholder
                width="100%"
                height={80}
                marginBottom={smallScreenDetected ? 8 : 12}
            />
            <SkeletonPlaceholder
                width="60%"
                height={20}
                marginBottom={smallScreenDetected ? 4 : 8}
            />
            <SkeletonPlaceholder width="80%" height={20} />
        </Wrapper>
    );
};

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
});

const Icon = styled(DsIcon)({
    position: 'absolute',
    width: pxToRem(28),
    height: pxToRem(28),
    left: 'auto',
    right: pxToRem(4),
    top: pxToRem(4),
});

const ImageContainer = styled.div({
    height: pxToRem(112),
    overflow: 'hidden',
    [mediaQuery.maxWidth('xl')]: {
        height: pxToRem(80),
    },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const Image = styled.img({
    maxWidth: '100%',
    maxHeight: '100%',
});

const CircleImage = styled.img({
    width: pxToRem(84),
    height: pxToRem(84),
    borderRadius: '50%',
    [mediaQuery.maxWidth('xl')]: {
        width: pxToRem(68),
        height: pxToRem(68),
    },
});

const ContentWrapper = styled.div({
    marginTop: pxToRem(12),
    [mediaQuery.maxWidth('md')]: {
        marginTop: pxToRem(8),
    },
});
